import * as THREE from "three";
import {CheckOverObstArr, GetClickObj, GetDis, GetVerPosArr, resolveVerFloorDepth, computeVerFloorDepthFromCornerRays} from "./Common";
import {SetDelIconUI, SetupInputObstUI, SetupSwitchObstRot} from "./TransControl";
import {SwitchModuleIrrMat, SetMaxFillForRoofs, addModulesToRoofAutoFill} from "./ModuleControl";
import {useViewerStore} from "../../../../../store/store";
import {SetMeshOBB, UpdateMeshOBB} from "./MeshControl";
import {createPolygonExtrudeGeometry} from "./Loader";
import { Viewer } from "../Viewer-Factory/Viewer";
import { NodePos, TreeGroup } from "../../../../../@types/types";
import { removeSelectedTree } from "../../../../../helpers/storeHelpers";
import { AlertType, EditAction, TreeProperty } from "../../../../../@types/enums";
import { DebugDrawManager, getOneDrawDebuggerOptions, ClearDebugDraw } from "./DebugDraw";
import { canGhostTreeSelection, isGhostOnlyGreenImpactTreesEnabled } from "./EPCControl";
import { applyTreeGhostAppearance, applyTreeSelectionAppearance, syncAllTreeSelectionHighlights } from "./TreeGhostVisual";
import {
    armGhostReevalDeferral,
    beginManualGhostToggle,
    clearGhostReevalDeferral,
    clearManualGhostFrozenOnOtherGhostedTrees,
    setManualGhostFrozen,
} from "./TreeImpactEligibility";

const delOpacity = 0.3;

export function ToggleSelectedTreeDelete(self: Viewer) {
    const firstGroup = self.selTreeArr[0];
    if (!firstGroup) return;
    const allDelStatusTrue = self.selTreeArr.every(group => group.delStatus === true);
    const newStatus = !allDelStatusTrue;

    if (newStatus && isGhostOnlyGreenImpactTreesEnabled() && !canGhostTreeSelection(self.selTreeArr)) {
        self.viewerAlert({
            show: true,
            title: "Cannot ghost tree",
            message: "Only trees with green impact multipliers can be ghosted.",
            messageType: AlertType.warning,
            isCloseAfterDisplay: true,
            autoCloseSeconds: 3000,
        });
        return;
    }

    const treeIds: string[] = [];

    // Manual ghost toggles must not re-evaluate ghosted trees during the shade chain they trigger;
    // relative multipliers shift and incorrectly auto-un-ghost trees that still qualify.
    if (isGhostOnlyGreenImpactTreesEnabled()) {
        beginManualGhostToggle();
        const selectedTreeIds = new Set(self.selTreeArr.map(group => group.userData?.treeId));
        clearManualGhostFrozenOnOtherGhostedTrees(self.treeGroup.children, selectedTreeIds);
        self.treeGroup.children.forEach((tree: TreeGroup) => {
            if (tree.delStatus && !selectedTreeIds.has(tree.userData?.treeId)) {
                armGhostReevalDeferral(tree);
            }
        });
    }

    self.selTreeArr.forEach(group => {
        if (isGhostOnlyGreenImpactTreesEnabled()) {
            if (newStatus) {
                const impactAtGhost = group.treeImpact ?? 0;
                group.userData.impactAtGhost = impactAtGhost;
                group.userData.activeTreeImpact = impactAtGhost;
                group.userData.hasReevaluatedSinceGhost = false;
                setManualGhostFrozen(group, true);
                armGhostReevalDeferral(group);
            } else {
                clearGhostReevalDeferral(group);
                setManualGhostFrozen(group, false);
                delete group.userData.impactAtGhost;
                delete group.userData.activeTreeImpact;
                delete group.userData.hasReevaluatedSinceGhost;
            }
        }
        applyTreeGhostAppearance(group, newStatus);
        treeIds.push(group.userData.treeId);
    });

    syncAllTreeSelectionHighlights(self);
    SetDelIconUI('Tree', newStatus);
    self.reCalcShade(treeIds, newStatus);
    // User-initiated ghost / unghost: trigger a full shade refresh on top of the
    // incremental reCalcShade so downstream consumers tied to SHADE_CALCULATIONS
    // (production, irradiance, reports) re-run against fresh ray data.
    self.refreshShade();
}

export { applyTreeGhostAppearance } from "./TreeGhostVisual";

export function ChangeTreeDelete(treeGroup: TreeGroup, value: boolean) {
    applyTreeGhostAppearance(treeGroup, value);
}

export function ChangeSelectedTreeShape(self: Viewer, value: string) {
    const {selTreeArr, crownMapArr} = self,
        selCrownMap = crownMapArr.find(item => item.crownKey === value);
    if (!selCrownMap) return;
    const isSimpleTreeLook = useViewerStore.getState().isSimpleTreeLook;
    selTreeArr.forEach(group => {
        group.children[0].userData.crownKey = value;
        if (isSimpleTreeLook) return;
        group.children[0].material = new THREE.MeshStandardMaterial({
            color: 0x90EE90,
            map: selCrownMap,
            transparent: true,
            depthWrite: true,
            side: 1
        });
        if (group.select) applyTreeSelectionAppearance(group, true);
    });
    self.refreshShade();
}

export function ChangeTreeShape(self: Viewer, tree: any, crownKey: string) {
    const isSimpleTreeLook = useViewerStore.getState().isSimpleTreeLook;
    const selCrownMap = self.crownMapArr.find(item => item.crownKey === crownKey);
    tree.children[0].userData.crownKey = crownKey;
    if (isSimpleTreeLook) return;
    tree.children[0].material = new THREE.MeshStandardMaterial({
        color: 0x90EE90,
        map: selCrownMap,
        transparent: true,
        depthWrite: true,
        side: 1
    });
}

export function ChangeTreePos(self: Viewer, type: string, value: number) {
    const {treeTransMesh, treeGroup, roofMeshArr, cSize} = self, axis = type === 'posX' ? 'x' : 'z';
    // treeTransMesh.position[axis] = value;
    treeTransMesh!.position[axis] = value;
    const { position, oriPos } = treeTransMesh!,
        deltaDis = { x: position.x - oriPos.x, z: position.z - oriPos.z };
    treeGroup.children.forEach((group: any) => {
        if (!group.select) return;
        const newPos = { x: group.oriPos.x + deltaDis.x, z: group.oriPos.z + deltaDis.z };
        group.position.set(newPos.x, 0, newPos.z);
    });
    self.refreshShade();
}

export function getTreeDimensions(treeGroup: any) {
    const userData = treeGroup?.userData ?? {};
    const crownMesh = treeGroup?.children?.[0];
    const trunkMesh = treeGroup?.children?.[1];
    const crownScl = crownMesh?.crownScl ?? { x: 1, y: 1, z: 1 };
    return {
        height: treeGroup?.totalH ?? userData.height ?? 0,
        crown_radius: userData.crown_radius ?? (crownMesh ? crownMesh.scale.x / crownScl.x : 0),
        crown_height: userData.crown_height ?? (crownMesh ? crownMesh.scale.y / crownScl.y : 0),
        trunk_radius: userData.trunk_radius ?? trunkMesh?.scale?.x ?? 0,
    };
}

export function SetTreeSize(self: Viewer, treeGroup: any, treeInfo: any) {
    const { height, crown_radius, crown_height, trunk_radius } = treeInfo;
    const trunk_height = Math.max(0, height - crown_height);
    const crownMesh = treeGroup.children[0], trunkMesh = treeGroup.children[1], wrapperMesh = treeGroup.children[2];
    const {crownScl} = crownMesh;
    const isSimpleTreeLook = !!crownMesh?.userData?.isSimpleTreeLook;
    const crownDiameter = crown_radius;
    const crownHeight = crown_height;
    crownMesh.scale.set(crownDiameter * crownScl.x, crownHeight * crownScl.y, crownDiameter * crownScl.z);

    
    const crownTopY = height;
    let crownBottomY = crownTopY - Math.max(0, crownHeight);
    const crownGeometry = crownMesh.geometry as THREE.BufferGeometry | undefined;
    if (crownGeometry) {
        if (!crownGeometry.boundingBox) crownGeometry.computeBoundingBox();
        const bb = crownGeometry.boundingBox;
        if (bb) {
            const localMaxY = bb.max.y * crownMesh.scale.y;
            const localMinY = bb.min.y * crownMesh.scale.y;
            crownMesh.position.y = crownTopY - localMaxY;
            crownBottomY = crownMesh.position.y + localMinY;
        } else {
            crownMesh.position.y = crownTopY - crownHeight / 2;
        }
    } else {
        crownMesh.position.y = crownTopY - crownHeight / 2;
    }

    const tuck = isSimpleTreeLook ? Math.max(0, crownHeight) * 0.05 : trunk_height * 0.3;
    const trunkTopY = Math.min(crownTopY, Math.max(0, crownBottomY + tuck));
    trunkMesh.scale.set(trunk_radius, trunkTopY, trunk_radius);
    trunkMesh.position.y = trunkTopY / 2;

    wrapperMesh.scale.set(crown_radius / 2 * 1, height * 1, crown_radius / 2 * 1);
    wrapperMesh.position.y = height / 2;

    const shouldShowTreeCylinders = getOneDrawDebuggerOptions().fillDebugger.showTreeCylinders;
    wrapperMesh.visible = shouldShowTreeCylinders;
    if (shouldShowTreeCylinders) {
        wrapperMesh.material.color.setHex(0xFFFFFF);
        wrapperMesh.material.needsUpdate = true;
    }
    DebugDrawManager.execute(self, 'toggleTreeCylinders', { show: shouldShowTreeCylinders });

    treeGroup.totalH = height;
    // treeGroup.treeInfo = {...treeInfo};
    treeGroup.userData = { ...treeGroup.userData, height, crown_radius, crown_height, trunk_radius };
}

export function ChangeTreeSize(self: Viewer, type: string, value: number) {
    const { selTreeArr, pushMode, virTree } = self, virFlag = (pushMode && virTree?.visible);

    const apply = (group: any, height: number) => {
        const current = getTreeDimensions(group);
        SetTreeSize(self, group, {
            height,
            crown_radius: type === 'crown_radius' ? value : current.crown_radius,
            crown_height: type === 'crown_height' ? value : current.crown_height,
            trunk_radius: type === 'trunk_radius' ? value : current.trunk_radius,
        });
    };

    if (virFlag) {
        if (!virTree) return;
        const height = type === 'height' ? virTree.totalH * value : virTree.totalH;
        apply(virTree, height);
    } else if (!pushMode && selTreeArr.length) {
        const heightRatio = type === 'height' && selTreeArr[0].totalH ? value / selTreeArr[0].totalH : 1;
        selTreeArr.forEach(group => {
            const height = type === 'height' ? group.totalH * heightRatio : group.totalH;
            apply(group, height);
        });
    }
}

export function RemoveTreeFromCart(self: any) {
    if (!self.selTreeArr || self.selTreeArr.length === 0) return;
    let updatedCartTrees = useViewerStore.getState().treeCartItems;

    const treeIds: string[] = [];
    self.selTreeArr.forEach((treeInCart: any) => {
        treeInCart.userData.treeCartInfo = {};
        treeInCart.select = false;
        removeSelectedTree(treeInCart);
        applyTreeSelectionAppearance(treeInCart, false);
        treeIds.push(treeInCart.userData.treeId);

        //Remove Tree from cart based on selected tree id
        updatedCartTrees = updatedCartTrees.filter((tree) => tree.userData.treeId !== treeInCart.userData.treeId);

        ChangeTreeDelete(treeInCart, false);
    });

    useViewerStore.setState({ treeCartItems: updatedCartTrees });
    self.reCalcShade(treeIds, false);
    // User-initiated unghost via cart removal: trigger a full shade refresh so
    // SHADE_CALCULATIONS-dependent UI re-runs against fresh ray data.
    self.refreshShade();
}

export function GetObstTypeGeo(num: number, sides?: number, nodes?: NodePos[], normal_top?: { X: number; Y: number; Z: number }) {
    if (num === 1) return new THREE.BoxGeometry(1, 1, 1);
    else if (num === 2) return new THREE.CylinderGeometry(0.6, 0.6, 1, sides || 6);
    else if (num === 3) return new THREE.CylinderGeometry(0.6, 0.6, 1, 16);
    else if (num === 4) return createPolygonExtrudeGeometry(nodes, 1, normal_top);
}

export function SetBtnObstActive(shapeNum: number) {
    const btnObstTypeArr = document.getElementsByClassName('btn-obst-type');
    for (let i = 0; i < btnObstTypeArr.length; i++) {
        const element = btnObstTypeArr[i];
        element.classList.remove('active');
    }
    const selBtnObstType = document.getElementsByClassName('btnObstType' + shapeNum);
    if (!selBtnObstType || !selBtnObstType[0]) return;
    selBtnObstType[0].classList.add('active');
    // document.getElementById('obstControlCube').style.display = shapeNum === 1 ? 'block' : 'none';
    const obstControlCube = document.getElementById('obstControlCube');
    if (obstControlCube) {
        obstControlCube.style.display = shapeNum === 1 ? 'block' : 'none';
    }

    // document.getElementById('obstControlPolygon').style.display = shapeNum === 2 ? 'block' : 'none';
    const obstControlPolygon = document.getElementById('obstControlPolygon');
    if (obstControlPolygon) {
        obstControlPolygon.style.display = shapeNum === 2 ? 'block' : 'none';
    }

    // document.getElementById('obstControlCyliner').style.display = shapeNum === 3 ? 'block' : 'none';
    const obstControlCyliner = document.getElementById('obstControlCyliner');
    if (obstControlCyliner) {
        obstControlCyliner.style.display = shapeNum === 3 ? 'block' : 'none';
    }
}

function GetObstSel(self: Viewer) {
    const { obstGroup, obstId, pushMode, virObs } = self;
    let selGroup: any;
    if (obstId) {
        selGroup = obstGroup.children.find((item: any) => item.obstId === obstId);
    } else if (pushMode && virObs?.visible) {
        selGroup = virObs;
    } else {
        return {
            selGroup: null,
            obstMesh: null,
            obstFrame: null,
            inGroup: null,
        };
    }

    const inGroup = selGroup.children[0].children[0];

    return {
        selGroup,
        obstMesh: inGroup.children[0],
        obstFrame: inGroup.children[1],
        inGroup,
    }
}

function setObstDepth(
    self: Viewer,
    obstMesh: any,
    obstFrame: { scale: { y: number; }; position: { y: number; }; },
    obst?: THREE.Object3D,
) {
    const { height, verFloor } = obstMesh;

    if (!verFloor) {
        obstMesh.scale.y = obstFrame.scale.y = height;
        obstMesh.position.y = obstFrame.position.y = height / 2;
        refreshObstructionCornerDepthRays(self);
        return;
    }

    const depth = obst
        ? resolveVerFloorDepth(obst, obstMesh, self.roofMeshArr, height)
        : 0;

    obstMesh.scale.y = obstFrame.scale.y = height + depth;
    obstMesh.position.y = obstFrame.position.y = (height / 2) - depth;
    refreshObstructionCornerDepthRays(self);
}

export function refreshObstructionCornerDepthRays(self: Viewer) {
    const showRays = getOneDrawDebuggerOptions().fillDebugger.showObstructionCornerDepthRays;
    ClearDebugDraw(self, 'obstruction-corner-depth-rays');
    if (!showRays) return;

    self.obstGroup?.children?.forEach((obst: any) => {
        const obstMesh = obst?.children?.[0]?.children?.[0]?.children?.[0];
        if (!obstMesh?.verFloor || obst.isObtsructionOnGround || !obstMesh.height) return;

        const { rays } = computeVerFloorDepthFromCornerRays(obst, self.roofMeshArr, obstMesh.height);
        obstMesh.verFloorRays = rays;

        rays.forEach((ray: { corner: THREE.Vector3; length: number }) => {
            if (!ray?.corner || !ray.length) return;
            DebugDrawManager.execute(self, 'drawRayArrow', {
                origin: ray.corner.clone(),
                direction: new THREE.Vector3(0, -1, 0),
                length: ray.length,
                color: 0x00ff00,
                group: 'obstruction-corner-depth-rays',
            });
        });
    });
}

export function ChangeObstAngle(self: Viewer) {
    const { selGroup, obstMesh, inGroup, obstFrame } = GetObstSel(self);
    if (!selGroup) return;
    const { obstId, rotX } = obstMesh;
    const switchObstVer = document.getElementById('switchObstVer');
    const verFloor = !switchObstVer?.classList.contains('toggled');
    inGroup.rotation.x = verFloor ? 0 : rotX;
    obstMesh.verFloor = verFloor;
    setObstDepth(self, obstMesh, obstFrame, selGroup);
    SetupSwitchObstRot(verFloor);

    self.refreshShade();
    self.addStep(EditAction.ObstAngle, obstId);
}

export function ChangeObstHeight(self: Viewer, value: number) {
    const { selGroup, obstMesh, obstFrame } = GetObstSel(self);
    if (!selGroup) return;
    obstMesh.height = value;
    setObstDepth(self, obstMesh, obstFrame, selGroup);
    useViewerStore.setState({ obstUpdated: !useViewerStore.getState().obstUpdated });
}

export function ChangeObstWidth(self: Viewer, value: number, isUserUpdated = false) {
    const { selGroup, obstMesh, obstFrame } = GetObstSel(self);
    if (!selGroup) return;
    if (selGroup.isDefaultObstruction) {
        selGroup.userUpdatedDefaultDimensions = true;
    }
    obstMesh.width = value;
    obstMesh.radius = (obstMesh.width + obstMesh.length) / 2;
    obstMesh.diameter = obstMesh.radius;
    if (obstMesh.shapeNum === 1) obstMesh.scale.x = obstFrame.scale.x = value;
}

export function ChangeObstLength(self: Viewer, value: number, isUserUpdated = false) {
    const { selGroup, obstMesh, obstFrame } = GetObstSel(self);
    if (!selGroup) return;
    if (selGroup.isDefaultObstruction) {
        selGroup.userUpdatedDefaultDimensions = true;
    }
    obstMesh.length = value;
    obstMesh.radius = (obstMesh.width + value) / 2;
    obstMesh.diameter = obstMesh.radius;
    if (obstMesh.shapeNum === 1) {
        obstMesh.scale.z = obstFrame.scale.z = value;
        setObstDepth(self, obstMesh, obstFrame, selGroup);
    }
}

export function ChangeObstSides(self: Viewer, value: number) {
    const { selGroup, obstMesh, obstFrame } = GetObstSel(self);
    if (!selGroup) return;

    obstMesh.sides = value;
    if (obstMesh.shapeNum === 2) {
        obstMesh.geometry = obstFrame.geometry = GetObstTypeGeo(2, value);
        obstMesh.scale.x = obstFrame.scale.x = obstMesh.radius;
        obstMesh.scale.z = obstFrame.scale.z = obstMesh.radius;
        //Update Old edge mesh
        const edgeMesh: any = obstMesh.children[0];
        if (edgeMesh) edgeMesh.geometry = new THREE.EdgesGeometry(obstMesh.geometry);

    }
}

export function ChangeObstDiameter(self: Viewer, value: number) {
    const { selGroup, obstMesh, obstFrame } = GetObstSel(self);
    if (!selGroup) return;

    obstMesh.diameter = value;
    obstMesh.width = value;
    obstMesh.length = value;
    obstMesh.radius = value;
    if (obstMesh.shapeNum === 2) {
        obstMesh.scale.x = obstFrame.scale.x = value;
        obstMesh.scale.z = obstFrame.scale.z = value;
        setObstDepth(self, obstMesh, obstFrame, selGroup);
    }
}

export function ChangeObstRadius(self: Viewer, value: number, origin?: boolean) {
    const { selGroup, obstMesh, obstFrame } = GetObstSel(self);
    if (!selGroup) return;

    obstMesh.radius = value;
    obstMesh.width = value;
    obstMesh.length = value;
    obstMesh.diameter = value;
    if (obstMesh.shapeNum === 3 || origin) {
        obstMesh.scale.x = obstFrame.scale.x = value;
        obstMesh.scale.z = obstFrame.scale.z = value;
        setObstDepth(self, obstMesh, obstFrame, selGroup);
    }
}

/** Build extruded footprint in obstruction-local space (XZ plane, extruded along Y). */
function buildLocalFootprintGeometry(
    localPoints: THREE.Vector3[],
    height: number,
    extraDepth = 0,
): THREE.ExtrudeGeometry {
    const shape = new THREE.Shape();
    localPoints.forEach((p, i) => {
        if (i === 0) shape.moveTo(p.x, p.z);
        else shape.lineTo(p.x, p.z);
    });

    const geometry = new THREE.ExtrudeGeometry(shape, {
        depth: height + extraDepth,
        bevelEnabled: false,
    });
    geometry.rotateX(Math.PI / 2);

    const minY = Math.min(...localPoints.map((p) => p.y));
    geometry.translate(0, minY, 0);
    return geometry;
}

/**
 * Replace the axis-aligned box with a mesh that matches the four saved node positions.
 * Ignores corrupt saved rotation/width metadata from legacy saves.
 */
export function applyFootprintFromWorldNodes(
    obst: THREE.Object3D,
    nodes: { X: number; Y: number; Z: number }[],
    height: number,
    rotX: number,
    rotY: number,
    roofRotY: number,
    extraDepth = 0,
) {
    const inGroup = obst.children[0].children[0];
    const obstMesh = inGroup.children[0] as THREE.Mesh;
    const obstFrame = inGroup.children[1] as THREE.Mesh;

    const root = new THREE.Object3D();
    root.position.copy(obst.position);
    root.rotation.y = roofRotY;
    const outGroup = new THREE.Object3D();
    outGroup.rotation.y = rotY;
    root.add(outGroup);
    const localRoot = new THREE.Object3D();
    localRoot.rotation.x = rotX;
    outGroup.add(localRoot);
    root.updateMatrixWorld(true);

    const worldToLocal = new THREE.Matrix4().copy(localRoot.matrixWorld).invert();
    const localPoints = nodes.map((n) =>
        new THREE.Vector3(n.X ?? 0, n.Y ?? 0, n.Z ?? 0).applyMatrix4(worldToLocal),
    );

    const geometry = buildLocalFootprintGeometry(localPoints, height, extraDepth);

    if (obstMesh.geometry) obstMesh.geometry.dispose();
    if (obstFrame.geometry) obstFrame.geometry.dispose();
    obstMesh.geometry = geometry;
    obstFrame.geometry = geometry.clone();
    obstMesh.scale.set(1, 1, 1);
    obstFrame.scale.set(1, 1, 1);
    obstMesh.rotation.set(0, 0, 0);
    obstFrame.rotation.set(0, 0, 0);
    obstMesh.position.set(0, 0, 0);
    obstFrame.position.set(0, 0, 0);

    const edgeMesh: any = obstMesh.children[0];
    if (edgeMesh?.geometry) {
        edgeMesh.geometry.dispose();
        edgeMesh.geometry = new THREE.EdgesGeometry(geometry);
    }

    (obstMesh as any).selfRot = 0;
    (obstMesh as any).userData.footprintFromNodes = true;
    (obst as any).footprintFromNodes = true;
    (obstMesh as any).nodes = [...nodes];
    SetMeshOBB(obstMesh);
}

function isFootprintObstruction(obst: any, obstMesh: any): boolean {
    return !!(
        obst?.footprintFromNodes
        || obst?.repairedFromNodes
        || obstMesh?.userData?.footprintFromNodes
    );
}

export function CreateObstBoxMesh(nodes: any[]) {
    const shape = new THREE.Shape();
    const center = { x: nodes[0].X, z: nodes[0].Z };
    const height = nodes[0].Y;

    for (let nodeIdx = 1; nodeIdx < nodes.length; nodeIdx++) {
        center.x += nodes[nodeIdx].X;
        center.z += nodes[nodeIdx].Z;
    }

    center.x /= nodes.length;
    center.z /= nodes.length;

    for (let nodeIdx = 0; nodeIdx < nodes.length; nodeIdx++) {
        const x = nodes[nodeIdx].X - center.x;
        const z = nodes[nodeIdx].Z - center.z;

        if (nodeIdx === 0) {
            shape.moveTo(x, z);
        } else {
            shape.lineTo(x, z);
        }
    }

    const mesh = new THREE.Mesh(
        new THREE.ExtrudeGeometry(shape, {
            depth: height,
            bevelEnabled: false,
        }),
        new THREE.MeshStandardMaterial({ color: 0xD3D3D3 }),
    );

    mesh.rotation.x = Math.PI / 2;
    mesh.position.set(center.x, height, center.z);
    return mesh;
}

export function ChangeObstShape(self: Viewer, shapeNum: number, nodes?: any[], normal_top?: { X: number; Y: number; Z: number }) {
    const { selGroup, obstMesh, obstFrame } = GetObstSel(self);



    if (!selGroup) {
        SetBtnObstActive(shapeNum);
        return;
    }

    switch (shapeNum) {
        case 1:
            obstMesh.geometry = obstFrame.geometry = GetObstTypeGeo(shapeNum, obstMesh.sides);
            obstMesh.width = obstMesh.diameter;
            obstMesh.length = obstMesh.diameter;
            obstMesh.scale.x = obstFrame.scale.x = obstMesh.width;
            obstMesh.scale.z = obstFrame.scale.z = obstMesh.length;
            break;
        case 2:
            obstMesh.geometry = obstFrame.geometry = GetObstTypeGeo(shapeNum, obstMesh.sides);
            obstMesh.width = obstMesh.diameter;
            obstMesh.length = obstMesh.diameter;
            obstMesh.scale.x = obstFrame.scale.x = obstMesh.width;
            obstMesh.scale.z = obstFrame.scale.z = obstMesh.length;
            break;
        case 3:
            obstMesh.geometry = obstFrame.geometry = GetObstTypeGeo(shapeNum, obstMesh.sides);
            obstMesh.width = obstMesh.diameter;
            obstMesh.length = obstMesh.diameter;
            obstMesh.scale.x = obstFrame.scale.x = obstMesh.width;
            obstMesh.scale.z = obstFrame.scale.z = obstMesh.length;
            break;
        case 4:
            obstMesh.geometry = obstFrame.geometry = GetObstTypeGeo(shapeNum, obstMesh.sides, nodes, normal_top);
            obstMesh.userData.normal_top = normal_top;
            break;
    }

    const edgeMesh: any = obstMesh.children[0];
    if (edgeMesh) edgeMesh.geometry = new THREE.EdgesGeometry(obstMesh.geometry);

    obstMesh.shapeNum = shapeNum;
    SetMeshOBB(obstMesh);
    SetBtnObstActive(shapeNum);
    SetupInputObstUI(obstMesh, selGroup.position);

    self.refreshShade();
}

export function ChangeObstDelete(self: Viewer) {
    const { selGroup } = GetObstSel(self);
    if (!selGroup) return;
    DeleteObst(selGroup, self);
}

export function DeleteObst(virObs: any, self: Viewer) {
    const inGroup = virObs.children[0].children[0], obstMesh = inGroup.children[0];
    useViewerStore.setState({ isGhostObstructionUpdated: !useViewerStore.getState().isGhostObstructionUpdated });
    if (obstMesh.delStatus) virObs.visible = true;
    obstMesh.delStatus = !obstMesh.delStatus;
    obstMesh.material.transparent = obstMesh.delStatus;
    obstMesh.material.opacity = obstMesh.delStatus ? delOpacity : 1;
    obstMesh.material.needsUpdate = true;
    const obstId = obstMesh.obstId;
    SetDelIconUI('Obst', obstMesh.delStatus);
    SetMaxFillForRoofs(self, obstMesh.roofId);
    self.reCalcShade([obstId], obstMesh.delStatus);

}

export function CheckDragableObst(e: MouseEvent, self: Viewer) {
    const { selTypeMeshArr, camera, cSize, obstGroup, obstId } = self;
    const downObst: any = GetClickObj(e, selTypeMeshArr, camera, cSize);
    if (downObst && downObst.object && downObst.object.obstId === obstId) {
        const dragObst: any = obstGroup.children.find((obst: any) => obst.obstId === obstId),
            { rotX, rotY, roofId } = dragObst.children[0].children[0].children[0];
        dragObst.oldInfo = { object: { rotX, rotY, roofFaceId: roofId }, point: { ...dragObst.position } };
        return dragObst;
    } else return null;
}

export function SetDragObst(e: MouseEvent | TouchEvent, self: Viewer) {
    const { roofMeshArr, camera, cSize, dragObst, roofRotY, roofs, houseTransPos } = self;
    const interInfo = GetClickObj(e, [self.mapCustom!, ...roofMeshArr], camera, cSize);
    if (!interInfo) return;
    let isObstructionOnRoof = false;
    if (interInfo.object.name.includes('roof')) {
        isObstructionOnRoof = true;
    }

    SetObstPosition(dragObst, interInfo, roofRotY, roofs[0].faces, houseTransPos, isObstructionOnRoof, roofMeshArr, self);
}

export function PutDragObst(self: Viewer) {
    const { obstGroup, dragObst, roofRotY, roofs, moduleMeshArr, houseTransPos, roofMeshArr } = self;
    const verPosArr = GetVerPosArr(dragObst);
    const overFlag = CheckOverObstArr(obstGroup, moduleMeshArr, dragObst.roofId, verPosArr, dragObst.obstId);
    if (overFlag) {
        SetObstPosition(dragObst, dragObst.oldInfo, roofRotY, roofs[0].faces, houseTransPos, true, roofMeshArr, self);
    } else {
        dragObst.children[0].children[0].children[0].verPosArr = verPosArr;
        const { obstId, oldInfo, position, rotX, rotY, roofId } = dragObst;
        dragObst.isObtsructionOnGround = position.y <= 0.2;
        const newInfo: any = { point: { ...position }, object: { rotX, rotY } }
        self.addStep(EditAction.ObstMove, obstId, oldInfo, newInfo);
        self.refreshShade();
    }
    self.dragObst = null;
}

export function SetObstPosition(obstObj: any, interInfo: any, roofRotY: number, faces?: any, houseTransPos?: any, isObstructionOnRoof?: boolean, roofMeshArr?: THREE.Object3D[], viewer?: Viewer) {
    const { point, object } = interInfo, { rotX, rotY, roofFaceId } = object;
    const obstMesh = obstObj.children[0].children[0].children[0];
    if (isObstructionOnRoof) {
        if (obstMesh.shapeNum === 4) { return }
        obstMesh.rotX = rotX || 0;
        obstMesh.rotY = rotY || 0;
        obstMesh.roofId = obstObj.roofId = roofFaceId;
        obstMesh.roofPos = { x: point.x - houseTransPos?.x, y: point.y, z: point.z - houseTransPos?.z };
        obstObj.rotation.y = roofRotY || 0;
        obstObj.children[0].rotation.y = rotY || 0;
    } else {
        obstMesh.rotX = rotX || 0; //Set obstruction parallel to terrain
    }
    SetOutsideObstPos(obstObj, point, faces, roofMeshArr, viewer);
}

export function SetOutsideObstPos(obstObj: any, oriPos: { x: number, y: number, z: number }, faces?: any, roofMeshArr?: THREE.Object3D[], viewer?: Viewer) {
    const inGroup = obstObj.children[0].children[0], obstMesh = inGroup.children[0], obstFrame = inGroup.children[1];
    const { height, verFloor, rotX, length } = obstMesh;
    // obstMesh.outside = CheckOutsideFaces(faces, oriPos, width, length);
    if (obstMesh.outside) {
        const top = oriPos.y + height;
        inGroup.rotation.x = 0;
        obstMesh.scale.y = obstFrame.scale.y = top;
        obstMesh.position.y = obstFrame.position.y = top / 2;
        obstObj.position.set(oriPos.x, 0, oriPos.z);
        obstMesh.worldPos = { x: oriPos.x, y: 0, z: oriPos.z };
        obstObj.isObtsructionOnGround = true;
    } else {
        if (obstMesh.shapeNum === 4) { return }
        obstObj.position.set(oriPos.x, oriPos.y, oriPos.z);
        inGroup.rotation.x = verFloor ? 0 : rotX || 0;

        const depth = obstMesh.roofId && verFloor && roofMeshArr?.length
            ? resolveVerFloorDepth(obstObj, obstMesh, roofMeshArr, height)
            : (obstMesh.roofId && verFloor ? Math.tan(-rotX) * length / 2 : 0);

        obstMesh.scale.y = obstFrame.scale.y = height + depth;
        obstMesh.position.y = obstFrame.position.y = (height / 2) - depth;
        obstMesh.worldPos = { x: oriPos.x, y: oriPos.y, z: oriPos.z };
    }
    obstObj.posY = obstObj.position.y;
    if (viewer) refreshObstructionCornerDepthRays(viewer);
}

export function HideObject(self: Viewer, type: string, value: boolean) {
    const isTreeLabelVisible = useViewerStore.getState().isTreeLabelVisible;
    const isTreeMultiplierLabelVisible = useViewerStore.getState().isTreeMultiplierLabelVisible;
    if (type === 'GhostObst') {
        var flagChangeObst = false;
        self.obstGroup.children.forEach(group => {
            if (!group.children[0] || !group.children[0].children[0]) return;
            const obstMesh: any = group.children[0].children[0].children[0];
            if (obstMesh.delStatus) {
                group.visible = !value;
                flagChangeObst = true;
            }
        });
    } else if (type.includes('AllObst')) {
        const obstIds: string[] = [];
        self.obstGroup.children.forEach(group => {
            group.visible = !value;
            if (type === 'WhatIfAllObst') {
                const obstMesh: any = group.children[0]?.children[0]?.children[0];
                if (!obstMesh) return;
                if (value) {
                    obstMesh.oldDelStatus = obstMesh.delStatus;
                    obstMesh.delStatus = true;
                    obstIds.push(obstMesh.obstId);
                } else {
                    obstMesh.delStatus = obstMesh.oldDelStatus;
                    if (!obstMesh.oldDelStatus) obstIds.push(obstMesh.obstId);
                }
            }
        });
        self.reCalcShade(obstIds, value);
    } else if (type === 'GhostTree') {
        var flagChangeTree = false;
        self.treeGroup.children.forEach((group: any) => {
            if (group.delStatus) {
                if (isTreeMultiplierLabelVisible) {
                    self.treeLabelGroup.children.forEach(labelGroup => {
                        if (labelGroup.userData.treeid === group.treeId) {
                            labelGroup.visible = !value;
                        }
                    })
                }
                if (isTreeLabelVisible) {
                    self.treeTagGroup.children.forEach(tagGroup => {
                        if (tagGroup.name === group.treeId) {
                            tagGroup.visible = !value;
                        }
                    })
                }
                group.visible = !value;
                flagChangeTree = true;
            }
        });
    } else if (type === 'SelTree') {
        var flagChangeTree = false;
        self.treeGroup.children.forEach((group: any) => {
            if (group.select) {
                if (isTreeMultiplierLabelVisible) {
                    self.treeLabelGroup.children.forEach(labelGroup => {
                        if (labelGroup.userData.treeid === group.treeId) {
                            labelGroup.visible = !value;
                        }
                    })
                }
                if (isTreeLabelVisible) {
                    self.treeTagGroup.children.forEach(tagGroup => {
                        if (tagGroup.name === group.treeId) {
                            tagGroup.visible = !value;
                        }
                    })
                }
                group.visible = !value;
                flagChangeTree = true;
            }
        });
    } else if (type.includes('AllTrees')) {
        const obstIds: string[] = [];
        self.treeGroup.children.forEach((group: any) => {
            group.visible = !value;
            if (type === 'WhatIfAllTrees') {
                if (value) {
                    group.oldDelStatus = group.delStatus;
                    group.delStatus = true;
                    obstIds.push(group.userData.treeId);
                } else {
                    group.delStatus = group.oldDelStatus;
                    if (!group.oldDelStatus) obstIds.push(group.userData.treeId);
                }
            }
        });
        self.reCalcShade(obstIds, value);
    } else if (type === 'ModuleTexture') {
        SwitchModuleIrrMat(self, value);
    } else if (type === 'ModulePathway') {
        self.flagModulePathway = value;
        self.roofMeshArr.forEach(roofMesh => {
            addModulesToRoofAutoFill(self, roofMesh,null,null,true);
        })
    }
}

export function SetObstVPos(obstGroup: THREE.Group) {
    obstGroup.children.forEach((child: any) => {
        child.vPos = new THREE.Box3().setFromObject(child);
    });
}

function getLongestEdgeAngle(points: THREE.Vector3[]): number {
    let bestAngle = 0;
    let bestLen = 0;
    for (let i = 0; i < points.length; i++) {
        const a = points[i];
        const b = points[(i + 1) % points.length];
        const dx = b.x - a.x;
        const dz = b.z - a.z;
        const lenSq = dx * dx + dz * dz;
        if (lenSq > bestLen) {
            bestLen = lenSq;
            bestAngle = Math.atan2(dz, dx);
        }
    }
    return bestAngle;
}

function normalizeAngle(a: number): number {
    while (a > Math.PI) a -= 2 * Math.PI;
    while (a <= -Math.PI) a += 2 * Math.PI;
    return a;
}

const NODE_BOX_ALIGNMENT_EPS = 0.35;

function scoreNodeBoxAlignment(
    nodePoints: THREE.Vector3[],
    boxPoints: THREE.Vector3[],
): number {
    return nodePoints.reduce((sum, node) => {
        let minDist = Infinity;
        for (const corner of boxPoints) {
            minDist = Math.min(minDist, node.distanceTo(corner));
        }
        return sum + minDist;
    }, 0);
}

/** Swap width/length when saved metadata used the wrong axis (legacy saves). */
export function CheckDefaultObst(obstArr: any[]) {
    obstArr.forEach((obst) => AlignObstFootprintToNodes(obst));
}

/**
 * Align a shape-1 obstruction box to its four saved nodes.
 * Searches rotations and width/length swaps so corrupt saved rotation metadata is ignored.
 */
export function AlignObstFootprintToNodes(obst: any): boolean {
    const obstMesh = obst.children[0].children[0].children[0];
    const obstFrame = obst.children[0].children[0].children[1];

    if (isFootprintObstruction(obst, obstMesh)) {
        return true;
    }

    if (obstMesh.shapeNum !== 1 || obstMesh.nodes?.length !== 4) {
        return false;
    }

    const nodePoints = obstMesh.nodes.map(
        (n: { X: number; Z: number }) => new THREE.Vector3(n.X, 0, n.Z),
    );
    const { width, length } = obstMesh;
    const origScaleX = obstMesh.scale.x;
    const origScaleZ = obstMesh.scale.z;

    const baseBoxPoints = obstMesh.verPosArr.map(
        (v: { x: number; z: number }) => new THREE.Vector3(v.x, 0, v.z),
    );
    const edgeAngle = normalizeAngle(
        getLongestEdgeAngle(baseBoxPoints) - getLongestEdgeAngle(nodePoints),
    );
    const angleCandidates = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2, edgeAngle];

    let bestScore = Infinity;
    let bestAngle = 0;
    let bestSwap = false;

    for (const swap of [false, true]) {
        if (swap) {
            obstMesh.width = length;
            obstMesh.length = width;
            obstMesh.scale.x = obstFrame.scale.x = length;
            obstMesh.scale.z = obstFrame.scale.z = width;
        } else {
            obstMesh.width = width;
            obstMesh.length = length;
            obstMesh.scale.x = obstFrame.scale.x = origScaleX;
            obstMesh.scale.z = obstFrame.scale.z = origScaleZ;
        }

        for (const candidateAngle of angleCandidates) {
            obstMesh.rotation.y = candidateAngle;
            obstFrame.rotation.y = candidateAngle;
            const boxPoints = GetVerPosArr(obst).map(
                (v: { x: number; z: number }) => new THREE.Vector3(v.x, 0, v.z),
            );
            const score = scoreNodeBoxAlignment(nodePoints, boxPoints);
            if (score < bestScore) {
                bestScore = score;
                bestAngle = candidateAngle;
                bestSwap = swap;
            }
        }
    }

    if (bestSwap) {
        obstMesh.width = length;
        obstMesh.length = width;
        obstMesh.scale.x = obstFrame.scale.x = length;
        obstMesh.scale.z = obstFrame.scale.z = width;
    } else {
        obstMesh.width = width;
        obstMesh.length = length;
        obstMesh.scale.x = obstFrame.scale.x = origScaleX;
        obstMesh.scale.z = obstFrame.scale.z = origScaleZ;
    }

    obstMesh.rotation.y = bestAngle;
    obstFrame.rotation.y = bestAngle;
    obstMesh.selfRot = bestAngle;
    obstMesh.verPosArr = GetVerPosArr(obst);

    return bestScore <= NODE_BOX_ALIGNMENT_EPS * nodePoints.length;
}

export function AdjustObstOrientation(obst: any, roofMeshArr?: THREE.Object3D[]) {
    const outGroup = obst.children[0];
    const inGroup = outGroup.children[0];
    const obstMesh = inGroup.children[0];

    if (isFootprintObstruction(obst, obstMesh)) {
        obstMesh.verPosArr = GetVerPosArr(obst);
        return;
    }

    const nodes = obstMesh.nodes ?? obstMesh.userData?.nodes;
    if (obstMesh.shapeNum === 1 && nodes?.length === 4) {
        const rotX = inGroup.rotation.x;
        const rotY = outGroup.rotation.y;
        const roofRotY = obst.rotation.y;
        applyFootprintFromWorldNodes(
            obst,
            nodes,
            obstMesh.height,
            rotX,
            rotY,
            roofRotY,
        );
        if (obstMesh.verFloor && obst.roofId && roofMeshArr?.length) {
            const extraDepth = resolveVerFloorDepth(obst, obstMesh, roofMeshArr, obstMesh.height);
            if (extraDepth > 0) {
                applyFootprintFromWorldNodes(
                    obst,
                    nodes,
                    obstMesh.height,
                    rotX,
                    rotY,
                    roofRotY,
                    extraDepth,
                );
            }
        }
        obstMesh.verPosArr = GetVerPosArr(obst);
        SetMeshOBB(obstMesh);
        return;
    }

    AlignObstFootprintToNodes(obst);
}

export function CheckModulesOverlay(self: Viewer) {
    const { meshArr, moduleMeshArr } = self;
    const obstructionMeshArr = meshArr.filter(mesh => mesh.meshType == 'obst');

    for (const obstruction of obstructionMeshArr) {
        UpdateMeshOBB(obstruction);

        for (const module of moduleMeshArr) {
            UpdateMeshOBB(module);

            const alignedModuleObb = module.userData.obb.clone();
            alignedModuleObb.center.y = obstruction.userData.obb.center.y;

            const intersects = obstruction.userData.obb.intersectsOBB(alignedModuleObb);

            if (intersects) {
                obstruction.material.transparent = true;
                obstruction.material.opacity = 0.5;
                obstruction.material.needsUpdate = true;
                break;
            } else {
                obstruction.material.transparent = false;
                obstruction.material.opacity = 1;
                obstruction.material.needsUpdate = true;
            }
        }
    }
}

export function SetObstProperty(self: Viewer, type: string, value: any) {
    switch (type) {
        case 'angle':
            return ChangeObstAngle(self);
        case 'height':
            return ChangeObstHeight(self, value);
        case 'length':
            return ChangeObstLength(self, value);
        case 'width':
            return ChangeObstWidth(self, value);
        case 'sides':
            return ChangeObstSides(self, value);
        case 'diameter':
            return ChangeObstDiameter(self, value);
        case 'radius':
            return ChangeObstRadius(self, value);
        case 'shape':
            return ChangeObstShape(self, value);
        case 'delete':
            return ChangeObstDelete(self);
    }
}

export function SetTreeProperty(self: Viewer, type: TreeProperty, value: any) {
    if (type === TreeProperty.posX || type === TreeProperty.posY) ChangeTreePos(self, type, value);
    else if (type === TreeProperty.delete) ToggleSelectedTreeDelete(self);
    else if (type === TreeProperty.shape) ChangeSelectedTreeShape(self, value);
    else ChangeTreeSize(self, type, value);
}